import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import Joi from "joi";
import SelectWithSearch from "@/features/shared/components/SelectWithSearch/SelectWithSearch";
import { fetchAbsenceTypes } from "@/features/absenceType/services/absenceTypeService";
const schema = Joi.object({
    type: Joi.string().trim().required().messages({
        "string.empty": "Le type est obligatoire.",
        "any.required": "Le type est obligatoire.",
    }),
    start_at: Joi.date().required().messages({
        "date.base": "La date de début est invalide.",
        "any.required": "La date de début est obligatoire.",
    }),
    end_at: Joi.date().min(Joi.ref("start_at")).required().messages({
        "date.base": "La date de fin est invalide.",
        "date.min": "La date de fin doit être postérieure ou égale au début.",
        "any.required": "La date de fin est obligatoire.",
    }),
    reason: Joi.string().allow("", null).max(500).messages({
        "string.max": "La raison ne doit pas dépasser 500 caractères.",
    }),
});
const toDateInputValue = (value) => {
    if (!value)
        return "";
    const date = new Date(value);
    const pad = (num) => `${num}`.padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    return `${year}-${month}-${day}`;
};
// Compute tomorrow's date in local time and format it for <input type="date">
const getTomorrowDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return toDateInputValue(d.toISOString());
};
const getStartPeriodFromValue = (value) => {
    if (!value)
        return "morning";
    const hours = new Date(value).getHours();
    return hours >= 13 ? "afternoon" : "morning";
};
const getEndPeriodFromValue = (value) => {
    if (!value)
        return "afternoon";
    const hours = new Date(value).getHours();
    return hours <= 12 ? "morning" : "afternoon";
};
const combineDateWithPeriod = (date, period, position) => {
    const timeByPeriod = {
        morning: { start: "08:00", end: "12:00" },
        afternoon: { start: "13:00", end: "17:00" },
    };
    return `${date}T${timeByPeriod[period][position]}`;
};
const formatWithOffset = (value) => {
    if (!value)
        return "";
    const date = new Date(value);
    const pad = (num) => `${Math.trunc(Math.abs(num))}`.padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
    const offsetRemainingMinutes = pad(Math.abs(offsetMinutes) % 60);
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetRemainingMinutes}`;
};
const resolver = async (values) => {
    const { error, value } = schema.validate(values, { abortEarly: false });
    if (!error) {
        return { values: value, errors: {} };
    }
    const formErrors = error.details.reduce((acc, curr) => ({
        ...acc,
        [curr.path[0]]: { type: curr.type, message: curr.message },
    }), {});
    return { values: {}, errors: formErrors };
};
const TimeOffForm = ({ initialValues, onSubmit, onCancel, submitLabel = "Enregistrer", beforeSubmitActions, }) => {
    const defaultStartDate = toDateInputValue(initialValues?.start_at) || getTomorrowDate();
    const defaultEndDate = toDateInputValue(initialValues?.end_at) || defaultStartDate;
    const defaultStartPeriod = getStartPeriodFromValue(initialValues?.start_at);
    const defaultEndPeriod = getEndPeriodFromValue(initialValues?.end_at);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [startPeriod, setStartPeriod] = useState(defaultStartPeriod);
    const [endPeriod, setEndPeriod] = useState(defaultEndPeriod);
    const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue, } = useForm({
        resolver,
        defaultValues: {
            type: initialValues?.type ?? "",
            // Par défaut: demain matin pour start_at et demain après-midi pour end_at
            start_at: combineDateWithPeriod(defaultStartDate, defaultStartPeriod, "start"),
            end_at: combineDateWithPeriod(defaultEndDate, defaultEndPeriod, "end"),
            reason: initialValues?.reason ?? "",
        },
    });
    const selectedType = watch("type");
    useEffect(() => {
        if (endDate < startDate) {
            setEndDate(startDate);
            setEndPeriod("afternoon");
        }
    }, [endDate, startDate]);
    useEffect(() => {
        setValue("start_at", combineDateWithPeriod(startDate, startPeriod, "start"), {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [setValue, startDate, startPeriod]);
    useEffect(() => {
        setValue("end_at", combineDateWithPeriod(endDate, endPeriod, "end"), {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [endDate, endPeriod, setValue]);
    const { data: absenceTypesData, isLoading: absenceTypesLoading, error: absenceTypesError } = useQuery(["absence-types", "time-off-form"], () => fetchAbsenceTypes(1, { active: "true" }, true, 100), {
        staleTime: 5 * 60 * 1000,
    });
    const absenceTypeOptions = useMemo(() => absenceTypesData?.absenceTypes ?? [], [absenceTypesData]);
    const selectedAbsenceType = useMemo(() => absenceTypeOptions.find((type) => type.label === selectedType || type.code === selectedType) ?? null, [absenceTypeOptions, selectedType]);
    const onSubmitHandler = async (values) => {
        const payload = {
            ...values,
            start_at: formatWithOffset(values.start_at),
            end_at: formatWithOffset(values.end_at),
            reason: values.reason?.trim() ? values.reason.trim() : null,
        };
        await onSubmit(payload);
    };
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmitHandler), className: "space-y-4 p-4", children: [_jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-sm font-medium", children: "Type de cong\u00E9" }), _jsx("input", { type: "hidden", ...register("type") }), _jsx(SelectWithSearch, { label: "S\u00E9lectionnez un type", options: absenceTypeOptions, selectedItem: selectedAbsenceType, onSelect: (option) => setValue("type", option.label, {
                            shouldValidate: true,
                            shouldDirty: true,
                        }), getOptionLabel: (option) => option.label, error: Boolean(errors.type) }), absenceTypesLoading && _jsx("p", { className: "text-sm text-gray-500", children: "Chargement des types d'absence\u2026" }), absenceTypesError && (_jsx("p", { className: "text-sm text-red-600", children: "Impossible de charger les types d'absence." })), errors.type && _jsx("p", { className: "text-sm text-red-600", children: errors.type.message })] }), _jsxs("div", { className: "grid gap-6 sm:grid-cols-2", children: [_jsx("input", { type: "hidden", ...register("start_at") }), _jsx("input", { type: "hidden", ...register("end_at") }), _jsxs("div", { className: "space-y-3", children: [_jsx("label", { className: "block text-sm font-medium", children: "Date de d\u00E9but" }), _jsxs("div", { className: "rounded border p-3 shadow-sm", children: [_jsx("input", { type: "date", value: startDate, onChange: (event) => setStartDate(event.target.value), className: "w-full rounded border p-2 focus:border-blue-500 focus:outline-none" }), _jsxs("div", { className: "mt-3 flex flex-wrap gap-4 text-sm", children: [_jsxs("label", { className: "inline-flex items-center gap-2", children: [_jsx("input", { type: "radio", name: "start-period", value: "morning", checked: startPeriod === "morning", onChange: () => setStartPeriod("morning"), className: "h-4 w-4" }), _jsx("span", { children: "Le matin" })] }), _jsxs("label", { className: "inline-flex items-center gap-2", children: [_jsx("input", { type: "radio", name: "start-period", value: "afternoon", checked: startPeriod === "afternoon", onChange: () => setStartPeriod("afternoon"), className: "h-4 w-4" }), _jsx("span", { children: "L\u2019apr\u00E8s-midi" })] })] })] }), errors.start_at && _jsx("p", { className: "text-sm text-red-600", children: errors.start_at.message })] }), _jsxs("div", { className: "space-y-3 sm:border-l sm:pl-4", children: [_jsx("label", { className: "block text-sm font-medium", children: "Date de fin" }), _jsxs("div", { className: "rounded border p-3 shadow-sm", children: [_jsx("input", { type: "date", value: endDate, min: startDate, onChange: (event) => setEndDate(event.target.value), className: "w-full rounded border p-2 focus:border-blue-500 focus:outline-none" }), _jsxs("div", { className: "mt-3 flex flex-wrap gap-4 text-sm", children: [_jsxs("label", { className: "inline-flex items-center gap-2", children: [_jsx("input", { type: "radio", name: "end-period", value: "morning", checked: endPeriod === "morning", onChange: () => setEndPeriod("morning"), className: "h-4 w-4" }), _jsx("span", { children: "Le matin" })] }), _jsxs("label", { className: "inline-flex items-center gap-2", children: [_jsx("input", { type: "radio", name: "end-period", value: "afternoon", checked: endPeriod === "afternoon", onChange: () => setEndPeriod("afternoon"), className: "h-4 w-4" }), _jsx("span", { children: "L\u2019apr\u00E8s-midi" })] })] })] }), errors.end_at && _jsx("p", { className: "text-sm text-red-600", children: errors.end_at.message })] })] }), _jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-sm font-medium", children: "Raison" }), _jsx("textarea", { ...register("reason"), className: "w-full rounded border p-2 focus:border-blue-500 focus:outline-none", placeholder: "Contexte partag\u00E9 avec votre manager", rows: 3 }), errors.reason && _jsx("p", { className: "text-sm text-red-600", children: errors.reason.message })] }), _jsxs("div", { className: "flex justify-end space-x-3", children: [beforeSubmitActions, _jsx("button", { type: "button", onClick: onCancel, className: "rounded border px-4 py-2 text-gray-700 hover:bg-gray-50", children: "Annuler" }), _jsx("button", { type: "submit", disabled: isSubmitting, className: "rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60", children: isSubmitting ? "Enregistrement..." : submitLabel })] })] }));
};
export default TimeOffForm;
